import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { checkedTranslations, type MyWord } from '@entities/Word';
import { cn } from '@shared/lib';
import { ChevronRightIcon } from '@shared/ui';

/** Пометка — как «Повторить N слов» у модуля: новое, повторить, сегодня; «недавно» — без пометки. */
function TrainingMark({ training }: { training: MyWord['training'] }) {
  const { t } = useTranslation();
  if (training === 'new') {
    return (
      <span className="inline-flex h-5 items-center rounded-pill bg-accent/[0.16] px-2 text-[11px] font-bold tracking-[0.02em] text-accent-hi">
        {t('words.list.status.new')}
      </span>
    );
  }
  if (training === 'fresh') return null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] leading-4',
        training === 'repeat' ? 'text-accent-hi' : 'text-success',
      )}
    >
      <span aria-hidden className={cn('h-[7px] w-[7px] rounded-full', training === 'repeat' ? 'bg-accent' : 'bg-success')} />
      {t(`words.list.status.${training}`)}
    </span>
  );
}

/** Колонки таблицы «Слов» на компьютере: слово с транскрипцией, перевод, модуль (если слова не одного модуля),
 *  пометка, стрелка. Заголовки колонок — в WordsSection по той же сетке. */
export const tableColumns = (withModule: boolean) =>
  withModule
    ? 'grid-cols-[minmax(0,1.05fr)_minmax(0,1.35fr)_minmax(0,0.85fr)_84px_16px]'
    : 'grid-cols-[minmax(0,1.05fr)_minmax(0,1.9fr)_84px_16px]';

/**
 * Строка «Слов» — коротко; вся строка — кнопка: стрелка справа и отклик на наведение и нажатие показывают, что
 * подробности (пример, смысл) — по нажатию (владелец 2026-10-28). Компьютер — таблицей, как в наброске: слово
 * с транскрипцией, перевод, модуль, пометка; телефон — слово с транскрипцией, под ним перевод, справа пометка.
 */
export const WordLine = memo(function WordLine({
  word: w,
  fresh,
  layout,
  showModule,
  onOpen,
}: {
  word: MyWord;
  /** Добавлено в этом заходе — подсветка, как у новой записи. */
  fresh: boolean;
  layout: 'table' | 'stacked';
  /** Модуль в строке не нужен, когда показаны слова одного модуля. */
  showModule: boolean;
  onOpen: (word: MyWord) => void;
}) {
  const { t } = useTranslation();
  const translations = checkedTranslations(w.meanings?.[0]?.senses ?? [], w.sessionSenseIds ?? []) || w.ru;
  const shell = cn(
    'group w-full rounded-[14px] px-3 text-left outline-none transition-colors',
    'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent active:bg-elev [@media(hover:hover)]:hover:bg-card',
    fresh && 'bg-accent/[0.07] shadow-[inset_3px_0_0_#F36A1C] [@media(hover:hover)]:hover:bg-accent/[0.11]',
  );
  const aria = t('words.list.rowAria', { word: w.word, translations, module: w.module.title });
  const chevron = (
    <ChevronRightIcon
      aria-hidden
      className="h-4 w-4 flex-none text-muted transition-transform duration-150 group-hover:translate-x-0.5 group-hover:text-fg motion-reduce:transition-none"
    />
  );

  if (layout === 'table') {
    return (
      <li>
        <button
          type="button"
          onClick={() => onOpen(w)}
          aria-label={aria}
          className={cn(shell, 'grid min-h-[56px] items-center gap-4 py-2', tableColumns(showModule))}
        >
          <span className="min-w-0">
            <span lang="en" className="block truncate font-serif text-[17px] leading-[22px] text-fg">
              {w.word}
            </span>
            {w.ipa && <span className="block truncate font-serif text-[13px] leading-[18px] text-muted">{w.ipa}</span>}
          </span>
          <span className="line-clamp-2 min-w-0 text-[14px] leading-5 text-[#cfcac3]">{translations}</span>
          {showModule && <span className="min-w-0 truncate text-[13px] leading-5 text-muted">{w.module.title}</span>}
          <span className="flex min-w-0">
            <TrainingMark training={w.training} />
          </span>
          {chevron}
        </button>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(w)}
        aria-label={aria}
        className={cn(shell, 'flex min-h-[60px] items-center gap-3 py-2.5')}
      >
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-baseline gap-2">
            <span lang="en" className="min-w-0 truncate font-serif text-[17px] leading-[22px] text-fg">
              {w.word}
            </span>
            {w.ipa && <span className="min-w-0 flex-none truncate font-serif text-[13px] leading-[18px] text-muted max-w-[45%]">{w.ipa}</span>}
          </span>
          {translations && <span className="mt-0.5 block truncate text-[14px] leading-5 text-[#cfcac3]">{translations}</span>}
        </span>
        <span className="flex min-w-0 max-w-[40%] flex-none flex-col items-end gap-1">
          <TrainingMark training={w.training} />
          {showModule && <span className="max-w-full truncate text-[12px] leading-4 text-muted">{w.module.title}</span>}
        </span>
        {chevron}
      </button>
    </li>
  );
});
